/**
 * Utilidades de texto: slug, escapado y limpieza.
 *
 * Nota de seguridad: el renderer nunca interpola HTML coming del usuario.
 * Construye el HTML desde una lista blanca de nodos y escapa todo el texto.
 * `escapeHtml` es la unica puerta por la que pasa el contenido authored.
 */

/** Quita acentos y baja a ASCII estable para generar anchors y slugs. */
export function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** Escapa texto para interpolarlo en HTML como contenido, nunca como markup. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);
}

/**
 * Valida URLs de enlaces e imágenes.
 *
 * Acepta solo `http`, `https`, `mailto`, `tel` y rutas internas relativas.
 * Rechaza `javascript:`, `data:`, `vbscript:` y cualquier esquema raro — que
 * es exactamente el vector de XSS que sobrevive a un sanitizer ingenuo.
 */
export function safeUrl(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;

  // Protocol-relative y rutas relativas internas son seguras.
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  if (value.startsWith("#")) return value;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(value)) return null;

  const protocol = value.slice(0, value.indexOf(":")).toLowerCase();
  if (["http", "https", "mailto", "tel"].includes(protocol)) return value;
  return null;
}

export function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

export function isInternalUrl(url: string): boolean {
  const value = url.trim();
  return value.startsWith("/") && !value.startsWith("//");
}

/** Quita el sufijo de marca que `Layout.astro` vuelve a agregar. */
export function stripBrandSuffix(value: string | null | undefined): string {
  return (value ?? "").replace(/\s·\s*Vimos Digital\s*$/, "").trim();
}

/** Normaliza saltos de linea mixtos (CRLF de Word/Docs) a LF. */
export function normalizeNewlines(value: string): string {
  return value.replace(/\r\n?/g, "\n");
}

/**
 * Limpia texto que viene pegado desde Word/Docs/ChatGPT en modo texto plano.
 * No inventa estructura: solo normaliza para que la heuristica pueda leerlo.
 */
export function cleanPastedPlainText(value: string): string {
  return normalizeNewlines(value)
    .replace(/\u00a0/g, " ")
    .replace(/[\u200b-\u200d\ufeff]/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Divide un texto en parrafos significativos, ignorando lineas vacias. */
export function splitParagraphs(value: string): string[] {
  return normalizeNewlines(value)
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\s+$/g, ""))
    .filter((paragraph) => paragraph.trim().length > 0);
}

export function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}
